import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { ActionIcon, type ActionIconProps } from '@mantine/core'
import { IconMicrophone, IconPlayerStop } from '@tabler/icons-react'

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike

type SpeechRecognitionLike = {
  lang: string
  interimResults: boolean
  maxAlternatives: number
  onresult: ((event: SpeechRecognitionResultEventLike) => void) | null
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null
  onend: (() => void) | null
  start: () => void
  stop: () => void
  abort?: () => void
}

type SpeechRecognitionResultEventLike = {
  resultIndex: number
  results: {
    length: number
    [index: number]: {
      isFinal: boolean
      [index: number]: {
        transcript: string
      }
    }
  }
}

type SpeechRecognitionErrorEventLike = {
  error: string
}

type SpeechRecognitionWindow = Window &
  typeof globalThis & {
    SpeechRecognition?: SpeechRecognitionConstructor
    webkitSpeechRecognition?: SpeechRecognitionConstructor
  }

export type VoiceInputButtonHandle = {
  stop: () => Promise<void>
}

type VoiceInputButtonProps = {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  language?: string
  onErrorChange?: (error: string | null) => void
  size?: ActionIconProps['size']
}

const getSpeechRecognition = () => {
  const speechWindow = window as SpeechRecognitionWindow
  return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition
}

const getMicErrorMessage = (error: string) => {
  if (error === 'not-allowed' || error === 'service-not-allowed') {
    return 'Microphone permission denied. Please allow access in browser settings.'
  }

  if (error === 'audio-capture') {
    return 'No microphone was found.'
  }

  if (error === 'no-speech') {
    return 'No speech detected. Try again.'
  }

  return `Microphone error: ${error}`
}

type Session = {
  recognition: SpeechRecognitionLike
  stopResolvers: Array<() => void>
}

export const VoiceInputButton = forwardRef<VoiceInputButtonHandle, VoiceInputButtonProps>(
  ({ value, onChange, disabled = false, language = 'en-US', onErrorChange, size = 'lg' }, ref) => {
    const [speechSupported] = useState(() => Boolean(getSpeechRecognition()))
    const [listening, setListening] = useState(false)
    const activeSessionRef = useRef<Session | null>(null)
    const speechBaseRef = useRef('')

    const updateError = useCallback(
      (error: string | null) => {
        onErrorChange?.(error)
      },
      [onErrorChange],
    )

    const stopRecognition = useCallback(() => {
      const session = activeSessionRef.current
      if (!session) {
        setListening(false)
        return Promise.resolve()
      }

      setListening(false)

      return new Promise<void>((resolve) => {
        session.stopResolvers.push(resolve)
        session.recognition.stop()
      })
    }, [])

    useImperativeHandle(ref, () => ({ stop: stopRecognition }), [stopRecognition])

    useEffect(() => {
      if (disabled) {
        void stopRecognition()
      }
    }, [disabled, stopRecognition])

    useEffect(() => {
      return () => {
        const session = activeSessionRef.current
        if (!session) return

        session.recognition.onresult = null
        session.recognition.onerror = null
        session.recognition.onend = null
        session.recognition.abort?.()
        activeSessionRef.current = null

        const resolvers = session.stopResolvers
        session.stopResolvers = []
        resolvers.forEach((resolve) => resolve())
      }
    }, [])

    const toggleMic = useCallback(() => {
      if (disabled) return

      if (listening) {
        void stopRecognition()
        return
      }

      const SpeechRecognition = getSpeechRecognition()
      if (!SpeechRecognition) {
        updateError('Voice input is not supported in this browser.')
        return
      }

      updateError(null)

      const recognition = new SpeechRecognition()
      recognition.lang = language
      recognition.interimResults = true
      recognition.maxAlternatives = 1

      const session: Session = { recognition, stopResolvers: [] }
      activeSessionRef.current = session
      speechBaseRef.current = value.trim()

      let finalTranscript = ''

      recognition.onresult = (event) => {
        if (activeSessionRef.current !== session) return

        let interimTranscript = ''

        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const result = event.results[i]
          const transcript = result[0]?.transcript.trim() ?? ''

          if (result.isFinal) {
            finalTranscript = [finalTranscript, transcript].filter(Boolean).join(' ')
          } else {
            interimTranscript = [interimTranscript, transcript].filter(Boolean).join(' ')
          }
        }

        const speechText = [finalTranscript, interimTranscript].filter(Boolean).join(' ')
        onChange([speechBaseRef.current, speechText].filter(Boolean).join(' '))
      }

      const drainStopResolvers = () => {
        const resolvers = session.stopResolvers
        session.stopResolvers = []
        resolvers.forEach((resolve) => resolve())
      }

      recognition.onerror = (event) => {
        updateError(getMicErrorMessage(event.error))
        if (activeSessionRef.current === session) {
          activeSessionRef.current = null
          setListening(false)
        }
        drainStopResolvers()
      }

      recognition.onend = () => {
        if (activeSessionRef.current === session) {
          activeSessionRef.current = null
          setListening(false)
        }
        drainStopResolvers()
      }

      try {
        recognition.start()
        setListening(true)
      } catch {
        activeSessionRef.current = null
        setListening(false)
        updateError('Voice input could not be started.')
      }
    }, [disabled, language, listening, onChange, stopRecognition, updateError, value])

    if (!speechSupported) {
      return null
    }

    return (
      <ActionIcon
        color={listening ? 'red' : 'gray'}
        variant={listening ? 'light' : 'subtle'}
        radius="xl"
        size={size}
        disabled={disabled}
        onClick={toggleMic}
        aria-label={listening ? 'Stop voice input' : 'Voice input'}
        aria-pressed={listening}
        title={listening ? 'Stop listening' : 'Voice input'}
        className={listening ? 'animate-pulse' : undefined}
      >
        {listening ? <IconPlayerStop size={16} /> : <IconMicrophone size={16} />}
      </ActionIcon>
    )
  },
)

VoiceInputButton.displayName = 'VoiceInputButton'
